/**
 * Product Management System - Moon Moon Agency v2.1
 * Shopify product page with variant selection, media updates, and AJAX
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const PRODUCT_CONFIG = {
	// API Settings
	debounceDelay: 300,
	apiTimeout: 10000,
	
	
	// Selectors
	selectors: {
		variantSelector: 'variant-selector',
		productForm: '#product-form',
		variantInput: '[data-selected-variant-id]',
		productReload: '[data-product-reload]',
		variantFormValue: '[data-variant-value]',
		variantCheckbox: '[data-variant-input]:checked'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */

// Product Event Emitter — wrapper autour de window.emit pour le préfixe `product:` et cancelable
const productEmit = (event, detail = {}, cancelable = false) => {
	return window.emit(`product:${event}`, { event, data: detail, source: 'product' }, cancelable);
};

// DOM Selectors Cache for global elements
const ProductDOM = {
	get stickyCTA() { return document.querySelector('[data-sticky-cta]'); },
	get classicCTA() { return document.querySelector('[data-cta]'); },
	get priceElements() { return document.querySelectorAll('[data-price]'); }
};

// Product Data Management
const ProductManager = {
	updateVariantId(container, variantId) {
		if (!variantId) return;
		
		const variantInput = container?.querySelector(PRODUCT_CONFIG.selectors.variantInput);
		if (variantInput && variantInput.value !== variantId.toString()) {
			const oldValue = variantInput.value;
			variantInput.value = variantId;
			window.log('info', 'Product', 'Variant ID updated', { 
				oldVariantId: oldValue, 
				newVariantId: variantId,
				inputElement: variantInput.id || 'no-id',
				containerElement: container?.id || 'no-id'
			});
			productEmit('variant-input-updated', { variantId });
		}
	},

	updateReloadElements(html) {
		const elementsToUpdate = document.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);
		const newElements = html.querySelectorAll(PRODUCT_CONFIG.selectors.productReload);

		productEmit('product-reload-start', { elementsCount: elementsToUpdate.length });

		// Capturé avant le swap : le switch accessoire (.mm-acc) et les CTA affichant son prix
		// (.mm-pcta, .mm-sticky-cta) sont des [data-product-reload] distincts remplacés indépendamment
		// dans la boucle ci-dessous — l'ordre entre eux n'est pas garanti.
		const wasAccessoryChecked = !!document.querySelector('.mm-form-control__input[name="accessory"]')?.checked;

		let updatedCount = 0;

		elementsToUpdate.forEach((currentElement, index) => {
			// Try to match by ID first, then by specific class, fallback to index
			let newElement = null;
			
			if (currentElement.id) {
				newElement = html.getElementById(currentElement.id);
			} else if (currentElement.className) {
				// Match by specific component classes for better targeting
				const classList = Array.from(currentElement.classList);
				const specificClass = classList.find(cls => 
					cls.includes('component-price') || 
					cls.includes('component-quantity') || 
					cls.includes('component-cta') ||
					cls.includes('sticky-cta')
				);
				
				if (specificClass) {
					const candidates = html.querySelectorAll(`.${specificClass}[data-product-reload]`);
					newElement = candidates[0];
				}
			}
			
			// Fallback to index-based matching
			if (!newElement) {
				newElement = newElements[index];
			}
			
			if (newElement && this.shouldUpdateElement(currentElement, newElement)) {
				try {
					currentElement.outerHTML = newElement.outerHTML;
					updatedCount++;
					productEmit('product-reload-element-updated', {
						elementIndex: index,
						matchType: currentElement.id ? 'id' : (currentElement.className.includes('component') ? 'class' : 'index')
					});
				} catch (error) {
					window.log('warn', 'Product', 'Element update failed', { 
						index, 
						elementId: currentElement.id || 'no-id',
						elementTag: currentElement.tagName,
						elementClasses: currentElement.className,
						error: error.message 
					});
				}
			}
		});

		// .outerHTML sérialise les ATTRIBUTS, pas les propriétés JS modifiées en mémoire (ex. .checked
		// coché par clic utilisateur) : chaque bloc [data-product-reload] swappé (switch accessoire ET
		// CTA affichant son prix) retombe donc sur son état par défaut serveur. On réapplique le choix
		// utilisateur puis on redéclenche un vrai `change` pour que mm-accessory.js resynchronise le
		// prix affiché sur les CTA (déjà swappés à leur prix "produit seul" par défaut).
		if (wasAccessoryChecked) {
			const accessoryInput = document.querySelector('.mm-form-control__input[name="accessory"]');
			if (accessoryInput && !accessoryInput.disabled) {
				accessoryInput.checked = true;
				accessoryInput.dispatchEvent(new Event('change', { bubbles: true }));
			}
		}

		window.log('info', 'Product', 'DOM reload completed', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount,
			skippedElements: elementsToUpdate.length - updatedCount,
			elementTypes: [...new Set(Array.from(elementsToUpdate).map(el => el.tagName.toLowerCase()))]
		});
		
		productEmit('product-reload-complete', {
			totalElements: elementsToUpdate.length,
			updatedElements: updatedCount
		});
	},

	shouldUpdateElement(currentElement, newElement) {
		const currentContent = currentElement.innerHTML?.trim();
		const newContent = newElement.innerHTML?.trim();
		return currentContent !== newContent;
	}
};


/*********************************************************************************************
 * 🔄 VARIANT SELECTION LOGIC
 */
class VariantSelector extends HTMLElement {
	constructor() {
		super();
		this.cached = new Map();
		this.currentVariant = null;
		this.options = [];
		this.variantData = null;
		// Jeton de sequence : sur changements de variante rapides, ignore les reponses arrivant
		// dans le desordre (une reponse lente ne doit pas ecraser le DOM d'une variante plus recente).
		this._reqSeq = 0;
		
		this.container = this.closest(PRODUCT_CONFIG.selectors.productForm);
		if (!this.container) {
			window.log('error', 'Product', 'VariantSelector: No product form container found');
			return;
		}
		
		this.onVariantChange = this.onVariantChange.bind(this);
		this.init();
	}

	init() {
		productEmit('variant-selector-init-start');
		
		try {
			this.addEventListener("change", this.onVariantChange);
			this.getSelectedOptions();

			this.getSelectedVariant();
			
			productEmit('variant-selector-init-success');
		} catch (error) {
			window.log('error', 'Product', 'Failed to initialize VariantSelector', { error: error.message });
		}
	}

	onVariantChange() {
		productEmit('variant-change-start');

		try {
			setTimeout(() => {
				this.getSelectedOptions();
				this.getSelectedVariant();

				if (this.currentVariant) {
					productEmit('variant-found', {
						variantId: this.currentVariant.id,
						available: this.currentVariant.available
					});

					// Orchestrate all updates
					this.updateURL();
					this.updateProductData();
					this.updateMediaDisplay();
				} else {
					window.log('warn', 'Product', 'No matching variant found for options', { options: this.options });
					productEmit('variant-not-found', { options: this.options });
				}
			}, 10);
		} catch (error) {
			window.log('error', 'Product', 'Error in variant change', { error: error.message });
		}
	}

	/**
	 * Update product data
	 */
	async updateProductData() {
		// Update variant ID immediately
		ProductManager.updateVariantId(this.container, this.currentVariant.id);

		// Capture le jeton avant le fetch : si une selection plus recente survient pendant
		// l'await, reqId != this._reqSeq et on ignore cette reponse obsolete.
		const reqId = ++this._reqSeq;

		// Fetch and update product data
		const html = await this.fetchVariantData();
		if (reqId !== this._reqSeq) return;

		if (html) {
			ProductManager.updateReloadElements(html);
			this.updateVariantOptions(html);
		}
	}

	/**
	 * Update media display
	 */
	updateMediaDisplay() {
		// Use MediaManager from the separate media module
		if (window.MediaManager?.updateImagesVariant) {
			window.MediaManager.updateImagesVariant(this.currentVariant);
		} else {
			window.log('warn', 'Product', 'MediaManager or updateImagesVariant method not available', {
				mediaManagerExists: !!window.MediaManager,
				hasUpdateMethod: !!window.MediaManager?.updateImagesVariant
			});
		}
	}

	/**
	 * Fetch variant data
	 */
	async fetchVariantData() {
		if (!this.currentVariant?.id) return null;
		
		const baseUrl = this.dataset.url || window.location.pathname;
		const sectionId = this.dataset.section;
		
		if (!sectionId) {
			window.log('warn', 'Product', 'VariantSelector: No section ID provided');
			return null;
		}

		const variantUrl = `${baseUrl}?variant=${this.currentVariant.id}&section_id=${sectionId}`;

		// Check cache first
		if (this.cached.has(variantUrl)) {
			return this.cached.get(variantUrl);
		}

		try {
			// Timeout dur (apiTimeout) : evite une requete variante suspendue indefiniment
			// sur reseau instable (l'AbortError tombe dans le catch existant -> retourne null).
			const response = await fetch(variantUrl, { signal: AbortSignal.timeout(PRODUCT_CONFIG.apiTimeout) });
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			
			const responseText = await response.text();
			const html = new DOMParser().parseFromString(responseText, 'text/html');
			
			// Cache the result
			this.cached.set(variantUrl, html);
			
			productEmit('variant-fetch-success', { variantId: this.currentVariant.id });
			return html;
		} catch (error) {
			window.log('error', 'Product', 'Failed to fetch variant data', { error: error.message });
			return null;
		}
	}

	/**
	 * Get selected options from variant inputs
	 */
	getSelectedOptions() {
		const checkboxes = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantCheckbox);
		this.options = Array.from(checkboxes, checkbox => checkbox.value).filter(value => value);
	}

	/**
	 * Get the selected variant based on options
	 */
	getSelectedVariant() {
		const variants = this.getVariantJSON();
		
		if (!variants.length) {
			this.currentVariant = null;
			return;
		}

		this.currentVariant = variants.find(variant => {
			if (!variant.options || !Array.isArray(variant.options)) return false;
			return variant.options.every((option, index) => this.options[index] === option);
		});
	}

	/**
	 * Get variant data from embedded JSON
	 */
	getVariantJSON() {
		if (!this.variantData) {
			const jsonElement = this.querySelector('[type="application/json"]');
			
			if (!jsonElement) {
				window.log('error', 'Product', 'VariantSelector: No JSON data element found');
				return this.variantData = [];
			}
			
			try {
				const jsonText = jsonElement.textContent.trim();
				this.variantData = jsonText ? JSON.parse(jsonText) : [];
			} catch (error) {
				window.log('error', 'Product', 'Failed to parse variant JSON', { error: error.message });
				this.variantData = [];
			}
		}
		return this.variantData;
	}

	/**
	 * Update URL with selected variant
	 */
	updateURL() {
		if (!this.currentVariant?.id) return;
		
		try {
			const baseUrl = this.dataset.url || window.location.pathname;
			const url = new URL(baseUrl, window.location.origin);
			url.searchParams.set('variant', this.currentVariant.id);
			window.history.replaceState({}, '', url.toString());
		} catch (error) {
			window.log('error', 'Product', 'Failed to update URL', { error: error.message });
		}
	}

	/**
	 * Update variant options without breaking event listeners
	 */
	updateVariantOptions(html) {
		const currentOptions = this.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		const newOptions = html.querySelectorAll(PRODUCT_CONFIG.selectors.variantFormValue);
		
		if (!newOptions.length) return;

		currentOptions.forEach((currentOption, index) => {
			const newOption = newOptions[index];
			if (!newOption) return;

			const currentInput = currentOption.querySelector('input');
			const newInput = newOption.querySelector('input');
			
			if (currentInput && newInput) {
				// Update only disabled state, preserve checked state
				currentInput.disabled = newInput.disabled;
				
				const currentlyChecked = currentInput.checked;
				currentOption.className = newOption.className;
				
				if (currentlyChecked !== currentInput.checked) {
					currentInput.checked = currentlyChecked;
				}
			}
		});
	}
}

/*********************************************************************************************
 * 🏷️ UTILITY FUNCTIONS
 */

/**
 * Change quantity value and update prices
 */
function mmChangeQtty(delta, inputId) {
	const input = document.getElementById(inputId);
	if (!input) return;

	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;
	const minValue = parseInt(input.getAttribute('min'), 10) || 1;

	// Calculate new value with constraints
	const newValue = Math.max(minValue, Math.min(maxValue, currentValue + parseInt(delta, 10)));

	input.value = newValue;
	input.dispatchEvent(new Event('change', { bubbles: true }));

	// Update prices
	mmUpdatePrice(newValue);

	// Update button states
	updateQuantityButtonStates(input);
}

/**
 * Update plus button disabled state based on current quantity
 */
function updateQuantityButtonStates(input) {
	const currentValue = parseInt(input.value, 10) || 1;
	const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

	// Find the container
	const container = input.closest('.mm-qtty-container');
	if (!container) return;

	// Find plus button
	const plusBtn = container.querySelector('[data-qtty-increase]');

	// Update plus button only
	if (plusBtn) {
		if (currentValue >= maxValue) {
			plusBtn.setAttribute('disabled', '');
		} else {
			plusBtn.removeAttribute('disabled');
		}
	}
}

/**
 * Update displayed prices based on quantity
 */
function mmUpdatePrice(qtty) {
	const quantity = parseInt(qtty, 10);
	if (!quantity || quantity < 1) return;

	productEmit('quantity-update-start', { quantity });

	try {
		let updatedElementsCount = 0;

		// Update price elements
		ProductDOM.priceElements.forEach(priceElement => {
			const priceValue = parseFloat(priceElement.dataset.price);
			if (!isNaN(priceValue)) {
				const totalPrice = (priceValue * quantity) / 100;
				const currency = window.Shopify?.currency?.active || 'EUR';
				const locale = document.documentElement.lang || 'en';
				const formattedPrice = new Intl.NumberFormat(locale, {
					style: 'currency',
					currency: currency
				}).format(totalPrice).replace(/\u202f/g, '\u00a0');
				priceElement.textContent = formattedPrice;
				updatedElementsCount++;
			}
		});

		productEmit('quantity-update-success', { 
			quantity, 
			updatedElements: updatedElementsCount 
		});
		
		window.log('info', 'Product', 'Price update completed', {
			quantity,
			updatedPriceElements: updatedElementsCount
		});
	} catch (error) {
		window.log('error', 'Product', 'Failed to update prices', { 
			quantity, 
			error: error.message 
		});
		productEmit('quantity-update-error', { 
			quantity, 
			error: error.message 
		});
	}
}

/*********************************************************************************************
 * 📌 STICKY CTA & REASSURANCE
 */

/**
 * Initialize sticky CTA behavior
 */
function initializeStickyCTA() {
	const classicCTA = ProductDOM.classicCTA;
	const stickyCTA = ProductDOM.stickyCTA;

	if (!classicCTA || !stickyCTA) {
		window.log('warn', 'Product', 'StickyCTA: Required elements not found');
		return;
	}

	try {
		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				const isVisible = entry.isIntersecting;
				stickyCTA.classList.toggle('mm-none', isVisible);
			});
		}, { 
			threshold: 0,
			rootMargin: '0px 0px -10px 0px'
		});

		observer.observe(classicCTA);
		classicCTA._stickyObserver = observer;
		
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize sticky CTA', { error: error.message });
	}
}

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */

// Define custom element
customElements.define(PRODUCT_CONFIG.selectors.variantSelector, VariantSelector);

/*********************************************************************************************
 * 🚀 INITIALIZATION & ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	productEmit('initializing');

	try {
		initializeStickyCTA();
		productEmit('initialized');
		window.log('info', 'Product', 'Product page initialized successfully');
	} catch (error) {
		window.log('error', 'Product', 'Failed to initialize product page', { error: error.message });
	}

	// Initialize button states on page load
	const inputs = document.querySelectorAll('input[data-qtty-input]');
	inputs.forEach((input) => {
		updateQuantityButtonStates(input);
	});

	// Auto-ouverture du guide des tailles à l'arrivée depuis la quick-add modal
	// (?open=size-guide, cf. mm-quick-add-modal.liquid) : le lien "Guide des
	// tailles" n'y ouvre pas mm-size-guide directement — deux instances de
	// .mm-panel coexisteraient (le dialog quick-add + la sheet du guide) et
	// mm-panel.js n'autorise qu'un seul panel ouvert à la fois, fermant la
	// modale et perdant la sélection en cours. Sur la PDP il n'y a qu'un seul
	// panel en jeu, donc l'ouverture directe est sûre ici.
	if (new URLSearchParams(window.location.search).get('open') === 'size-guide') {
		const sizeGuidePanel = document.querySelector('[id^="SizeGuide-"]');
		if (sizeGuidePanel && window.MmPanel) window.MmPanel.open(sizeGuidePanel.id);
	}

	// Use event delegation for quantity buttons (works even after DOM updates)
	// Product page buttons use data-qtty-decrease/increase
	// Cart buttons use data-cart-decrease/increase (handled by mm_cart.js)
	document.addEventListener('click', (e) => {
		// Handle decrease buttons
		const decreaseBtn = e.target.closest('[data-qtty-decrease]');
		if (decreaseBtn) {
			const inputId = decreaseBtn.dataset.qttyDecrease;
			window.mmChangeQtty(-1, inputId);
			return;
		}

		// Handle increase buttons
		const increaseBtn = e.target.closest('[data-qtty-increase]');
		if (increaseBtn) {
			const inputId = increaseBtn.dataset.qttyIncrease;
			window.mmChangeQtty(1, inputId);
			return;
		}
	});

	// Adjust quantity when product reload completes
	document.addEventListener('product:product-reload-complete', () => {
		const inputs = document.querySelectorAll('input[data-qtty-input]');
		inputs.forEach((input) => {
			const currentValue = parseInt(input.value, 10) || 1;
			const maxValue = parseInt(input.getAttribute('max'), 10) || 9999;

			if (currentValue > maxValue) {
				input.value = maxValue;
			}

			updateQuantityButtonStates(input);
		});

		if (inputs.length > 0) {
			const newValue = parseInt(inputs[0].value, 10) || 1;
			mmUpdatePrice(newValue);
		}
	});
});